import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useHideModal, Stepper, useModalScroll, Container } from '@libs/components'
import { Close24 } from '@carbon/icons-react'
import { Button, message, Modal, Typography } from 'antd'
import { useLocalization } from '@libs/localization'
import { TaskModalCommonProps } from '../types'
import TaskModalTitle from 'modules/common/TaskModalTitle'
import ATTextDetailsForm from './ATTextDetailsForm'
import Styled from './Styled'
import { useNoteSubmitter } from '../hooks'
import { ITaskAITextDetailsForm, ITaskInputText } from '@libs/api/build/tasks/models'
import { useMutation, useQuery } from 'react-query'
import { TasksApi } from '@libs/api'
import QuestionnaireForm from 'modules/users/QustionnaireForm'
import QUERIES from 'modules/common/queries'

export interface TaskAITextDetailsModalProps extends TaskModalCommonProps {
	comtext?: boolean
	noStyleText?: boolean
}

const TaskAITextDetailsModal: FC<TaskAITextDetailsModalProps> = ({ task, onTaskComplete, onTaskCancel }) => {
	const hideModal = useHideModal()
	const { t } = useLocalization()
	const { scrollTo: scrollModalTo } = useModalScroll()
	const [currentStep, setCurrentStep] = useState(0)
	const [isLoading, setIsLoading] = useState(false)
	const [isQuestionnaireAvailable, setIsQuestionnaireAvailable] = useState(false)
	const [questionnaireError, setQuestionnaireError] = useState(false)
	const [detailsFormData, setDetailsFormData] = useState<ITaskAITextDetailsForm | null>(null)
	const [isQuestionnaireSubmitDisabled, setIsQuestionnaireSubmitDisabled] = useState(false)
	const [isQuestionnaireSubmitting, setIsQuestionnaireSubmitting] = useState(false)

	const { data: { isSendEmailAvailable } = {} } = useQuery(
		[QUERIES.questionnaire, task.id],
		() => TasksApi.getAITextQuestionnaire(task.id),
		{
			enabled: !!task.id,
			staleTime: 0,
			cacheTime: 0,
		}
	)

	useEffect(() => {
		if (isSendEmailAvailable === false) {
			setIsQuestionnaireAvailable(true)
		}
	}, [isSendEmailAvailable])

	const { submit, isLoading: isSubmitting } = useNoteSubmitter(task)

	const { mutateAsync: sendEmail, isLoading: isSendEmailLoading } = useMutation(
		TasksApi.sendEmailForAITextQuestionnaire
	)

	const mapInputTextToNote = useCallback(
		(data: ITaskAITextDetailsForm) => {
			const questionnaire = data.isQuestionnaireRequired
				? [
						t('common.terms.yes'),
						`${t('common.terms.name')}: ${data.contactName}`,
						`${t('common.terms.email')}: ${data.email}`,
				  ]
						.filter(Boolean)
						.join(', ')
				: t('common.terms.no')

			return [
				`${t('modals.aiTextDetails.inputArea.title')}: ${data.inputArea}`,
				`${t('common.terms.propertyType')}: ${data.propertyType}`,
				`${t('modals.aiTextDetails.shouldQuestionnaireBeConductedText')}: ${questionnaire}`,
				`${t('common.terms.responsibleAgent')}: ${data.responsibleAgent}`,
				`${t('common.terms.writingStyle')}: ${data.radioGroup}`,
			].join(', ')
		},
		[t]
	)

	const onDetailsSubmit = useCallback(async () => {
		const data = detailsFormData

		if (!data) {
			return
		}

		const inputText: ITaskInputText = {
			email: data.email || '',
			name: data.contactName || '',
			taskId: task.id,
		}

		let isSendEmailPosted = false

		if (data.isQuestionnaireRequired) {
			const emailResponse = await sendEmail(inputText)

			isSendEmailPosted = !!emailResponse?.success

			if (!isSendEmailPosted) {
				message.error(t('modals.aiTextDetails.failed'))
				return
			}
		}

		const isSubmitted = await submit(mapInputTextToNote(data))
		const isSuccess = data.isQuestionnaireRequired ? isSubmitted && isSendEmailPosted : isSubmitted

		if (isSuccess) {
			if (onTaskComplete) {
				onTaskComplete(task)
				return
			}

			hideModal()
		}
	}, [detailsFormData, submit, sendEmail, onTaskComplete, hideModal, mapInputTextToNote, t, task])

	const onDetailsComplete = useCallback(
		(data: ITaskAITextDetailsForm) => {
			setDetailsFormData(data)

			if (!isQuestionnaireAvailable) {
				onDetailsSubmit()
				return
			}

			setCurrentStep(1)
		},
		[isQuestionnaireAvailable, onDetailsSubmit, setCurrentStep]
	)

	const onQuestionnaireChange = useCallback(
		(isQuestionnaireSentByEmail: boolean) => {
			setIsQuestionnaireAvailable(!isQuestionnaireSentByEmail)
		},
		[setIsQuestionnaireAvailable]
	)

	const steps = useMemo(
		() => [
			{
				title: t('modals.aiTextDetails.inputArea.title'),
				content: (
					<ATTextDetailsForm
						task={task}
						setIsLoading={setIsLoading}
						onCompleteClick={onDetailsComplete}
						onQuestionnaireChange={onQuestionnaireChange}
						initialValues={detailsFormData}
						stepKey={currentStep}
						isSendEmailAvailable={isSendEmailAvailable}
					/>
				),
			},
			{
				title: t('modals.aiTextDetails.questionnaire.title'),
				content: questionnaireError ? (
					<Container display="flex" justifyContent="center">
						<Typography.Text type="danger" className="text-center">
							{t('users.pages.questionnaire.notFound')}
						</Typography.Text>
					</Container>
				) : (
					<QuestionnaireForm
						includeSubmitButton={false}
						setDisabledSubmitButton={setIsQuestionnaireSubmitDisabled}
						taskId={task.id}
						onSuccess={onDetailsSubmit}
						onError={() => setQuestionnaireError(true)}
						onAlreadySubmitted={() => setQuestionnaireError(true)}
						setIsSubmitting={setIsQuestionnaireSubmitting}
					/>
				),
			},
		],
		[
			task,
			setIsLoading,
			t,
			onDetailsSubmit,
			onQuestionnaireChange,
			questionnaireError,
			onDetailsComplete,
			detailsFormData,
			currentStep,
		]
	)

	const isModalLoading = isLoading || isSubmitting || isSendEmailLoading || isQuestionnaireSubmitting

	const DynamicButton = useMemo(() => {
		if (isQuestionnaireAvailable && currentStep === 0) {
			return (
				<Button type="primary" form="task-ai-text-details-form" htmlType="submit">
					{t('navigation.next')}
				</Button>
			)
		}
		if (questionnaireError && currentStep === 1) {
			return (
				<Button type="primary" onClick={() => setCurrentStep(0)}>
					{t('navigation.back')}
				</Button>
			)
		}
		return (
			<Button
				type="primary"
				form={currentStep === 0 ? 'task-ai-text-details-form' : 'ai-text-details-questionnaire-form'}
				htmlType="submit"
				disabled={isQuestionnaireSubmitDisabled}
				loading={isModalLoading}
			>
				{t('common.actions.completeTask')}
			</Button>
		)
	}, [isQuestionnaireAvailable, currentStep, t, questionnaireError, isModalLoading, isQuestionnaireSubmitDisabled])

	return (
		<Modal
			visible
			closeIcon={<Close24 />}
			title={<TaskModalTitle title={t('modals.aiTextDetails.title')} caseId={task.caseReference} />}
			onCancel={onTaskCancel ? () => onTaskCancel(task) : hideModal}
			footer={DynamicButton}
		>
			<Styled.CustomStepper current={currentStep} onTransitionComplete={() => scrollModalTo(0)}>
				{steps.map(({ title, content }, index) => (
					<Stepper.Step key={index} title={title}>
						{content}
					</Stepper.Step>
				))}
			</Styled.CustomStepper>
		</Modal>
	)
}

export default TaskAITextDetailsModal
